import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { clearPendingDomains, clearPermissioningCache, removePendingDomain, selectPendingDomains, selectSelectedAccountId, setPermissioningCache, useAppDispatch, useAppSelector, } from '@ibk/shared-config';
import { localStorageService, StorageKeys } from '@ibk/shared-utils';
import { useLazyGetDomainPermissioningQuery } from '../../services/permissioningApi';
import { ignoredRoutes } from '../../utils';
export default function useRootPermissioning() {
    const dispatch = useAppDispatch();
    const { pathname } = useLocation();
    const authToken = localStorageService.getItemSync(StorageKeys.AuthToken);
    const [fetchDomainPermissions] = useLazyGetDomainPermissioningQuery();
    const pendingDomains = useAppSelector(selectPendingDomains);
    const selectedAccountId = useAppSelector(selectSelectedAccountId);
    const [loadingDomains, setLoadingDomains] = useState([]);
    const processingDomainsRef = useRef(new Set());
    const isProcessingRef = useRef(false);
    const lastSelectedAccount = useRef(selectedAccountId);
    const addLoadingDomain = useCallback((domain) => {
        setLoadingDomains(prev => {
            if (prev.includes(domain))
                return prev;
            return [...prev, domain];
        });
    }, []);
    const removeLoadingDomain = useCallback((domain) => {
        setLoadingDomains(prev => prev.filter(load => load !== domain));
        processingDomainsRef.current.delete(domain);
    }, []);
    const fetchAuthorization = useCallback(async (domains) => {
        if (ignoredRoutes.includes(pathname) || isProcessingRef.current || !selectedAccountId || !authToken) {
            return;
        }
        isProcessingRef.current = true;
        try {
            const domainsToProcess = domains.filter(domain => !processingDomainsRef.current.has(domain));
            if (domainsToProcess.length === 0) {
                return;
            }
            domainsToProcess.forEach(domain => {
                processingDomainsRef.current.add(domain);
                addLoadingDomain(domain);
            });
            const promises = domainsToProcess.map(async (domain) => {
                try {
                    const { result } = await fetchDomainPermissions({
                        fromParty: selectedAccountId,
                        domain: domain,
                    }).unwrap();
                    if (result?.resources) {
                        dispatch(setPermissioningCache({ domain, resources: result.resources }));
                    }
                }
                catch (error) {
                    console.error(`Error fetching authorization for domain ${domain}:`, error);
                    dispatch(setPermissioningCache({ domain, resources: [] }));
                }
                finally {
                    dispatch(removePendingDomain({ domain }));
                    removeLoadingDomain(domain);
                }
            });
            await Promise.all(promises);
        }
        finally {
            isProcessingRef.current = false;
        }
    }, [selectedAccountId, pathname, addLoadingDomain, removeLoadingDomain, dispatch, fetchDomainPermissions]);
    useEffect(() => {
        if (pendingDomains.length > 0 && !isProcessingRef.current) {
            const delayDebounceFn = setTimeout(() => {
                fetchAuthorization(pendingDomains);
            }, 300);
            return () => clearTimeout(delayDebounceFn);
        }
    }, [pendingDomains, fetchAuthorization, pathname]);
    useEffect(() => {
        if (!authToken || !selectedAccountId || lastSelectedAccount.current !== selectedAccountId) {
            dispatch(clearPermissioningCache());
            dispatch(clearPendingDomains());
            lastSelectedAccount.current = selectedAccountId;
        }
    }, [authToken, selectedAccountId, dispatch]);
    useEffect(() => {
        if (ignoredRoutes.includes(pathname)) {
            dispatch(clearPermissioningCache());
            dispatch(clearPendingDomains());
        }
    }, [pathname, dispatch]);
    return {
        loading: loadingDomains.length > 0,
    };
}
